/**
 * What we say when loyalty rewards do not cover everything in a cart (D-122).
 *
 * One place, because this sentence appears at checkout, on the order, and in
 * the admin panel that causes it — and a customer who reads one wording on the
 * website and another in their confirmation concludes something went wrong.
 *
 * The reason is the true one and is said plainly: we present some events and we
 * host others for the people who put them on. Rewards are ours to give, so they
 * work on ours. Nobody has to know the word "rental" to understand that.
 */

/**
 * The named shows and how many there really are.
 *
 * The count comes from the deduplicated list, not the input: the same show
 * named twice is one show, and deciding "is" or "are" before deduplicating
 * produced "Gala are presented by someone else".
 */
const named = (names: readonly string[]): { text: string; count: number } => {
  const unique = [...new Set(names.filter(Boolean))];
  const text =
    unique.length === 0
      ? ''
      : unique.length === 1
        ? unique[0]!
        : unique.length === 2
          ? `${unique[0]} and ${unique[1]}`
          : `${unique.slice(0, -1).join(', ')} and ${unique[unique.length - 1]}`;
  return { text, count: unique.length };
};

const dollars = (cents: number): string => `$${(cents / 100).toFixed(2)}`;

/** The standing rule, for a place that has no particular event to name. */
export const LOYALTY_RULE = 'Loyalty rewards are earned and spent on the events we present ourselves.';

/**
 * Why a reward will not cover this whole order.
 *
 * `covered` is what it may be spent against. Naming the amount matters more
 * than naming the rule: somebody looking at a part-paid total wants to know
 * which part, and "$40.00 of this order" answers it in the words they are
 * already thinking in.
 */
export function rewardLimitedReason(excluded: readonly string[], coveredCents: number | null): string | null {
  const { text, count } = named(excluded);
  if (count === 0) return null;
  const amount = coveredCents !== null && coveredCents > 0 ? ` A reward covers up to ${dollars(coveredCents)} of this order.` : '';
  return `${LOYALTY_RULE} ${text} ${count === 1 ? 'is' : 'are'} presented by someone else, so a reward cannot be spent on ${count === 1 ? 'it' : 'them'}.${amount}`;
}

/** Why this order will not earn on all of itself. */
export function earnLimitedReason(excluded: readonly string[]): string | null {
  const { text, count } = named(excluded);
  if (count === 0) return null;
  return `${LOYALTY_RULE} ${text} ${count === 1 ? 'is' : 'are'} presented by someone else, so ${count === 1 ? 'it does' : 'they do'} not earn points.`;
}

/** Both at once, for a cart where the same events are out of both. */
export function loyaltyReasons(input: { earn_excluded: readonly string[]; redeem_excluded: readonly string[]; redeemable_cents: number | null }): string[] {
  const sameBoth =
    input.earn_excluded.length > 0 &&
    input.earn_excluded.length === input.redeem_excluded.length &&
    input.earn_excluded.every((name) => input.redeem_excluded.includes(name));
  if (sameBoth) {
    const { text, count } = named(input.earn_excluded);
    const amount = input.redeemable_cents !== null && input.redeemable_cents > 0 ? ` A reward covers up to ${dollars(input.redeemable_cents)} of this order.` : '';
    return [`${LOYALTY_RULE} ${text} ${count === 1 ? 'is' : 'are'} presented by someone else, so points are not earned or spent on ${count === 1 ? 'it' : 'them'}.${amount}`];
  }
  return [earnLimitedReason(input.earn_excluded), rewardLimitedReason(input.redeem_excluded, input.redeemable_cents)].filter((line): line is string => Boolean(line));
}
